/**
 * Loads what the Evals page needs: the runnable suites with their models and limits, what
 * waits for review, and the recent runs, kept current while one of them is active.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../services/apiClient';
import { evalsService } from '../services/evalsService';
import { isActiveRun, type EvalReviewQueue, type EvalRunItem, type ListEvalSuitesResponse } from '../types/evalTypes';

const POLL_MS = 10_000;

/** The page shows the newest runs only; the runs page has the rest. */
export const RECENT_RUNS = 10;

const errorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError && error.code === 'not-found') {
    return 'Evals are not enabled on this API. They run only on staging and on a local API.';
  }
  return error instanceof Error ? error.message : fallback;
};

export const useEvalsData = () => {
  const [catalog, setCatalog] = useState<ListEvalSuitesResponse | null>(null);
  const [runs, setRuns] = useState<EvalRunItem[]>([]);
  const [reviewQueue, setReviewQueue] = useState<EvalReviewQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [suites, recent, queue] = await Promise.all([evalsService.listSuites(), evalsService.listRuns(RECENT_RUNS), evalsService.getReviewQueue()]);
      setCatalog(suites);
      setRuns(recent.runs);
      setReviewQueue(queue);
    } catch (caught) {
      setError(errorMessage(caught, 'Failed to load evals.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // While a run in the list is active, read the list again every few seconds.
  const anyActive = runs.some((run) => isActiveRun(run.status));
  useEffect(() => {
    if (!anyActive) return;
    const timer = setInterval(() => {
      evalsService.listRuns(RECENT_RUNS).then((page) => setRuns(page.runs), () => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [anyActive]);

  return { catalog, runs, reviewQueue, loading, error, reload: load };
};
